import { cva } from 'class-variance-authority';

export const tabStyles = cva(['transition-colors', 'duration-150', 'ease-in-out', 'relative'], {
  variants: {
    active: {
      true: [],
      false: [],
    },
    disabled: {
      true: ['cursor-not-allowed'],
      false: ['cursor-pointer'],
    },
    intent: {
      default: ['py-[4px]', 'px-[6px]', 'text-body-regular'],
      old: ['h-[48px]', 'text-ltp-cn-body-bold', 'uppercase', 'whitespace-nowrap'],
      secondary: ['w-max', 'px-3', 'py-1', 'rounded-[4px]', 'leading-[150%]'],
      third: [
        'px-3',
        'py-1',
        'rounded-[4px]',
        'text-[16px]',
        'font-inter',
        'font-normal',
        'leading-[24px]',
        'whitespace-nowrap',
        'max-md:px-1',
        'max-md:text-[14px]',
        'max-md:leading-5',
      ],
      activeTop: [
        'py-[15px]',
        'px-[8px]',
        'h-[90px]',
        'justify-between',
        'uppercase !font-normal !text-[13px] !font-trade-pro',
      ],
      greyed_button: [
        'flex',
        'h-full',
        'w-full',
        'min-w-0',
        'items-center',
        'justify-center',
        'px-[18px]',
        'py-[8px]',
        'text-center',
        'text-body-small',
        'cursor-pointer',
        'max-md:w-auto',
        'max-md:shrink-0',
        'max-md:whitespace-nowrap',
        'max-md:px-3',
      ],
    },
  },
  compoundVariants: [
    {
      intent: 'default',
      active: true,
      class: ['text-blue-300'],
    },
    {
      intent: 'default',
      active: false,
      class: ['text-neutral-700 cursor-pointer'],
    },
    {
      intent: 'old',
      active: true,
      class: ['text-blue-300'],
    },
    {
      intent: 'old',
      active: false,
      class: ['text-neutral-700 cursor-pointer'],
    },
    {
      intent: 'secondary',
      disabled: true,
      active: false,
      class: ['bg-grey-300', 'text-grey-700', 'cursor-not-allowed', 'font-inter font-normal'],
    },
    {
      intent: 'secondary',
      disabled: false,
      active: false,
      class: ['bg-grey-300', 'text-grey-700', 'cursor-pointer'],
    },
    {
      intent: 'secondary',
      active: true,
      disabled: false,
      class: ['bg-blue-300', '!text-white', 'text-button-medium text-blue-900'],
    },
    {
      intent: 'secondary',
      active: false,
      disabled: false,
      class: ['bg-transparent', 'text-neutral-700', 'text-button-medium'],
    },
    {
      intent: 'third',
      active: true,
      disabled: false,
      class: ['bg-blue-300', '!text-white'],
    },
    {
      intent: 'third',
      active: false,
      disabled: false,
      class: ['bg-transparent', 'text-neutral-700'],
    },
    {
      intent: 'activeTop',
      active: false,
      class: ['cursor-pointer text-grey-700'],
    },
    {
      intent: 'activeTop',
      active: true,
      class: ['text-blue-300'],
    },
    {
      intent: 'greyed_button',
      active: false,
      disabled: false,
      class: ['bg-transparent', 'text-neutral-700', 'hover:text-neutral-600'],
    },
    {
      intent: 'greyed_button',
      active: true,
      disabled: false,
      class: [
        'cursor-pointer',
        'text-blue-300',
        'bg-white',
        'rounded-[6px]',
        'shadow-card',
      ],
    },
  ],
  defaultVariants: {
    active: false,
  },
});

export const tabIconStyles = cva([], {
  variants: {
    active: {
      true: ['text-blue-300'],
      false: [],
    },
    intent: {
      default: [],
      old: [],
      secondary: [],
      third: [],
      activeTop: [],
      greyed_button: 'w-6 h-6',
    },
  },
  defaultVariants: {
    intent: 'default',
  },
});

export const tabHighlightStyles = cva(['absolute', 'bottom-[-2px]', 'left-0', 'right-0', 'bg-blue-300'], {
  variants: {
    intent: {
      default: ['h-[2px]', 'rounded-[100px]'],
      old: ['h-[3px]'],
      secondary: ['hidden'],
      third: ['hidden'],
      activeTop: ['h-[8px]', 'bg-blue-900', 'absolute', '-top-0.5', 'left-0', 'right-0'],
      greyed_button: 'hidden',
    },
  },
  defaultVariants: {
    intent: 'default',
  },
});

export const tabsContainerStyles = cva(['flex'], {
  variants: {
    isHidden: {
      true: ['hidden'],
    },
    intent: {
      default: ['gap-x-3', 'border-b-[2px]', 'border-blue-100'],
      old: ['gap-x-[48px]', 'border-b-[3px]', 'border-blue-100'],
      secondary: ['gap-[2px]', 'w-fit', 'rounded-[6px]', 'border', 'border-blue-300', 'p-1'],
      third: [
        'gap-[2px]',
        'inline-flex',
        'rounded-[6px]',
        'outline',
        'outline-1',
        'outline-blue-300',
        '-outline-offset-1',
        'p-1',
        'max-md:flex',
        'max-md:w-full',
        'max-md:min-w-0',
        'max-md:[&>span]:flex',
        'max-md:[&>span]:min-w-0',
        'max-md:[&>span]:flex-1',
        'max-md:[&>span>button]:w-full',
        'max-md:[&>span>button]:min-w-0',
        'max-md:[&>span>button]:justify-center',
        'max-md:[&>span>button]:text-center',
      ],
      activeTop: ['gap-x-3', 'border-t-[2px]', 'border-blue-100', 'text-[13px] font-normal'],
      greyed_button: [
        'h-[52px]',
        'w-full',
        'rounded-[12px]',
        'gap-x-3',
        'bg-grey-100',
        'p-[6px]',
        '[&>span]:flex',
        '[&>span]:min-h-0',
        '[&>span]:min-w-0',
        '[&>span]:flex-1',
        'max-md:gap-x-1',
        'max-md:overflow-x-auto',
        'scrollbar-hide',
        'max-md:[&>span]:flex-none',
        'max-md:[&>span]:shrink-0',
      ],
    },
  },
  defaultVariants: {
    intent: 'default',
  },
});
